import { ArrowLeft, ExternalLink, Pencil, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Markdown } from '@/components/ui/markdown';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import { OPENING_STATUS_LABEL, type OpeningStatus } from '../constants';
import { useDeleteOpening, useOpening } from '../queries';

const STATUS_VARIANT: Record<OpeningStatus, 'secondary' | 'outline'> = {
  open: 'secondary',
  on_hold: 'outline',
  closed: 'outline',
  filled: 'secondary',
};

export function OpeningDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data: opening, isPending, isError, error } = useOpening(id);
  const remove = useDeleteOpening();

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/hrm/recruitment')}>
          <ArrowLeft /> Recruitment
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/hrm/recruitment')}>
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">{opening.title}</h1>
        <Badge variant={STATUS_VARIANT[opening.status]}>
          {OPENING_STATUS_LABEL[opening.status]}
        </Badge>
        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/hrm/recruitment/${opening.public_id}/edit`)}
          >
            <Pencil /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 /> Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete “{opening.title}”?</AlertDialogTitle>
                <AlertDialogDescription>
                  This opening will be removed. Existing candidates linked to it keep their record.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={remove.isPending}
                  onClick={() =>
                    remove.mutate(opening.id, { onSuccess: () => navigate('/hrm/recruitment') })
                  }
                >
                  {remove.isPending ? 'Deleting…' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
          <Field label="Department">{opening.department || '—'}</Field>
          <Field label="Headcount">{opening.openings_count}</Field>
          <Field label="Created">{new Date(opening.created).toLocaleDateString()}</Field>
          <Field label="External link">
            {opening.external_url ? (
              <a
                href={opening.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary inline-flex items-center gap-1 hover:underline"
              >
                <ExternalLink className="size-3.5" /> View posting
              </a>
            ) : (
              '—'
            )}
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Description</CardTitle>
        </CardHeader>
        <CardContent>
          {opening.description ? (
            <Markdown>{opening.description}</Markdown>
          ) : (
            <p className="text-muted-foreground text-sm">No description.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1">{children}</div>
    </div>
  );
}
